import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from 'react';
import log from 'loglevel';
import { viewPage } from 'fmc-sdk';
import { selectAnalytics, setAnalyticsState } from 'state/analytics';

import { useSelector, useDispatch } from 'react-redux';
import useRouter from 'use-react-router';

import window from 'window';
import useFmcHost from 'hooks/useFmcHost';
import canUseDOM from 'utils/canUseDOM';
import useAnalyticsVariables from './useAnalyticVariables';
import { map } from 'ramda';

export enum Event {
  ApplicationStart,
  ApplicationApproved,
  ApplicationPending,
  ApplicationDuplicate,
  ApplicationFailed,
  AddToWallet,
  ApplicationCompleted,
  TextToRecover,
  ApplyAndBuy,
  PrescreenOfferAccept,
  PrescreenOfferNoThanks,
  PrescreenOfferNotMe,
  PrequalOfferAccept,
  PrequalOfferDecline,
  PrequalOfferText,
  PrequalApplicationSubmit,
  PrequalApplicationReceive,
  PrequalRequestStarted,
  PrequalRequestCompleted,
  EnhancedCheckoutStarted,
  EnhancedCheckoutSuccessful,
  EnhancedCheckoutNotFound,
  EnhancedCheckoutError,
  InstallmentInitialOfferExpanded,
  SplitpayInitialOfferExpanded,
  PrequalInitialOfferExpanded,
  InstallmentOfferExpanded,
  SplitpayOfferExpanded,
  PrequalOfferExpanded,
  PrequalNotQualifiedExpanded,
  PrequalAccountFoundExpanded,
  PrequalFallbackExpanded,
  PageView,
  OrderSubmitted,
  OrderCompleted,
  SelectedCardChanged,
  IssuanceSucceeded,
  IssuanceFailed,
  FinancingTextMeMyOffers,
  IssuanceTimedOut,
  PrepareCheckoutSubmitted,
  CheckoutPrepared,
  IssuanceCallMade,
  IssuanceStatusPolled,
}

const eventMap: {
  [e: number]: {
    customEventDetailType: string;
  };
} = {
  [Event.ApplicationStart]: {
    customEventDetailType: 'application-start',
  },
  [Event.ApplicationApproved]: {
    customEventDetailType: 'application-approved',
  }, // dsp
  [Event.ApplicationPending]: {
    customEventDetailType: 'application-pending',
  }, // credit-pending
  [Event.ApplicationDuplicate]: {
    customEventDetailType: 'application-duplicate',
  }, // existing-acconut
  [Event.ApplicationFailed]: {
    customEventDetailType: 'application-failed',
  },
  [Event.AddToWallet]: {
    customEventDetailType: 'add-to-wallet',
  },
  [Event.ApplicationCompleted]: {
    customEventDetailType: 'application-completed',
  },
  [Event.TextToRecover]: {
    customEventDetailType: 'text-to-recover',
  },
  [Event.ApplyAndBuy]: {
    customEventDetailType: 'apply-and-buy',
  },
  [Event.PrescreenOfferAccept]: {
    customEventDetailType: 'prescreen-offer-acceptance',
  },
  [Event.PrescreenOfferNoThanks]: {
    customEventDetailType: 'prescreen-offer-no-thanks',
  },
  [Event.PrescreenOfferNotMe]: {
    customEventDetailType: 'prescreen-offer-not-me',
  },
  [Event.PrequalOfferAccept]: {
    customEventDetailType: 'prequal-offer-acceptance',
  },
  [Event.PrequalOfferDecline]: {
    customEventDetailType: 'prequal-offer-decline',
  },
  [Event.PrequalOfferText]: {
    customEventDetailType: 'prequal-offer-text',
  },
  [Event.EnhancedCheckoutStarted]: {
    customEventDetailType: 'enhanced-checkout-started',
  },
  [Event.EnhancedCheckoutSuccessful]: {
    customEventDetailType: 'enhanced-checkout-successful',
  },
  [Event.EnhancedCheckoutNotFound]: {
    customEventDetailType: 'enhanced-checkout-not-found',
  },
  [Event.EnhancedCheckoutError]: {
    customEventDetailType: 'enhanced-checkout-error',
  },
  [Event.InstallmentInitialOfferExpanded]: {
    customEventDetailType: 'installment-initial-offer-expanded',
  },
  [Event.SplitpayInitialOfferExpanded]: {
    customEventDetailType: 'splitpay-initial-offer-expanded',
  },
  [Event.PrequalInitialOfferExpanded]: {
    customEventDetailType: 'prequal-initial-offer-expanded',
  },
  [Event.InstallmentOfferExpanded]: {
    customEventDetailType: 'installment-offer-expanded',
  },
  [Event.SplitpayOfferExpanded]: {
    customEventDetailType: 'splitpay-offer-expanded',
  },
  [Event.PrequalOfferExpanded]: {
    customEventDetailType: 'prequal-offer-expanded',
  },
  [Event.PrequalNotQualifiedExpanded]: {
    customEventDetailType: 'prequal-not-qualified-expanded',
  },
  [Event.PrequalAccountFoundExpanded]: {
    customEventDetailType: 'prequal-account-found-expanded',
  },
  [Event.PrequalFallbackExpanded]: {
    customEventDetailType: 'prequal-fallback-expanded',
  },
  [Event.PrequalRequestStarted]: {
    customEventDetailType: 'prequal-request-started',
  },
  [Event.PrequalRequestCompleted]: {
    customEventDetailType: 'prequal-request-completed',
  },
  [Event.OrderSubmitted]: {
    customEventDetailType: 'order-submitted',
  },
  [Event.OrderCompleted]: {
    customEventDetailType: 'order-completed',
  },
  [Event.SelectedCardChanged]: {
    customEventDetailType: 'selected-card-changed',
  },
  [Event.IssuanceSucceeded]: {
    customEventDetailType: 'issuance-succeded',
  },
  [Event.IssuanceFailed]: {
    customEventDetailType: 'issuance-failed',
  },
  [Event.FinancingTextMeMyOffers]: {
    customEventDetailType: 'financing-text-me-my-offers',
  },
  [Event.IssuanceTimedOut]: {
    customEventDetailType: 'issuance-timeout-out',
  },
  [Event.PrepareCheckoutSubmitted]: {
    customEventDetailType: 'prepare-checkout-submitted',
  },
  [Event.CheckoutPrepared]: {
    customEventDetailType: 'checkout-prepared',
  },
  [Event.IssuanceCallMade]: {
    customEventDetailType: 'issuance-call-made',
  },
  [Event.IssuanceStatusPolled]: {
    customEventDetailType: 'issuance-status-polled',
  },
};

export type AnalyticsHook = () => {
  sendEvents: (events: Event[]) => void;
  sendPage: (name?: string) => void;
};

/**
 * Normalize pathname into "pageName"
 */
const usePageName = () => {
  const { location } = useRouter();
  return useMemo(() => location.pathname.replace(/^\//, ''), [location]);
};

/**
 * Wrap any function that requires the s object to be loaded
 * Return a function that will queue all calls until s is loaded. Then it will
 * call the original function with all of the queued arguments.
 *
 * Assume that on window `load` event, the s_code, if not blocked, will have been
 * loaded.
 */
// This function can wrap arbitrary function, so any is necessary.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDeferredAnalytics<T extends (...args: any[]) => any>(
  callback: T,
): (...args: Parameters<T>) => ReturnType<T> | void {
  const queue = useRef<unknown[][]>([]);
  const listenerRef = useRef<() => void | undefined>();
  const wrapped = useCallback(
    (...args) => {
      if (!canUseDOM) {
        return;
      }
      const hasSCode = () => Boolean(window.s);
      /**
       * If window is already loaded and window.s is still not available,
       * it must have been blocked/missing.
       */
      const isSCodeBlocked = () => {
        return document.readyState === 'complete' && !hasSCode();
      };

      if (hasSCode()) {
        return callback(...args);
      }
      if (isSCodeBlocked()) {
        return log.warn('s_code not loaded.');
      }

      queue.current.push(args);

      const listener = () => {
        if (listenerRef.current) {
          window.removeEventListener('load', listenerRef.current);
        }
        if (isSCodeBlocked()) {
          return log.warn('s_code not loaded.');
        }

        queue.current.forEach(args => {
          callback(...args);
        });
      };
      listenerRef.current = listener;
      window.addEventListener('load', listener);
    },
    [callback],
  );
  return wrapped;
}

const useAnalytics: AnalyticsHook = () => {
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const fmcDispatch = useFmcHost();

  const defaultPageName = usePageName();

  const sendCustomEvent = useCallback(
    (type: 'event-action-trigger' | 'page-view', detailType?: string) => {
      if (!canUseDOM) {
        return;
      }
      const customEvent = new CustomEvent(type, {
        detail: detailType ? { type: detailType } : {},
      });
      document.body.dispatchEvent(customEvent);
    },
    [],
  );

  const sendCustomEvents = useCallback(
    (events: Event[]): void => {
      events.forEach((event: Event) => {
        sendCustomEvent(
          'event-action-trigger',
          eventMap[event].customEventDetailType,
        );
      });
    },
    [sendCustomEvent],
  );

  const sendEvents = useCallback(
    (events: Event[]): void => {
      const names = map(e => Event[e], events);
      log.debug('Analytics', `Track event: ${names}`);
      sendCustomEvents(events);
    },
    [sendCustomEvents],
  );

  const sendPageViewCustomEvent = useCallback(
    (): void => sendCustomEvent('page-view'),
    [sendCustomEvent],
  );

  const sendPage = useDeferredAnalytics(
    useCallback(
      (pageName: string = defaultPageName): void => {
        log.debug('Analytics', `Track pageName: ${pageName}`);
        fmcDispatch(viewPage(pageName));
        setAnalyticsVariables({ pageName });
        sendPageViewCustomEvent();
      },
      [
        setAnalyticsVariables,
        sendPageViewCustomEvent,
        defaultPageName,
        fmcDispatch,
      ],
    ),
  );

  return { sendEvents, sendPage };
};

const useTrackApplicationStart = (pageName?: string, event?: Event) => {
  const { applicationStarted } = useSelector(selectAnalytics);
  const { sendEvents, sendPage } = useAnalytics();
  const defaultPageName = usePageName();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();

  useEffect(() => {
    const finalPageName = pageName || defaultPageName;
    if (applicationStarted) {
      sendPage(finalPageName);
    } else {
      sendPage(finalPageName);
      fmcDispatch(viewPage(finalPageName));
      sendEvents([event || Event.ApplicationStart]);
      dispatch(setAnalyticsState({ applicationStarted: true }));
    }
    // This should only run once when the page load. We don't need to
    // run it again when applicationStarted changes (because it is changed
    // inside this block)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const TrackedRouteContext = React.createContext(false);

/**
 * Route wrapper that track page view on every navigation event.
 *
 * It uses the pathname as the page name for analytics.
 *
 * It doesn't track if one of the ancestors is a TrackedRoute.
 * This is because we track on every location change, so having nested
 * TrackedRoute would track twice if we don't check the ancestors.
 */
const TrackedRoute: React.FC = ({ children }) => {
  const { sendPage } = useAnalytics();
  const { location } = useRouter();
  const hasTrackedRoute = useContext(TrackedRouteContext);

  useEffect(() => {
    if (!hasTrackedRoute && location) {
      sendPage();
    }
  }, [sendPage, location, hasTrackedRoute]);

  return (
    <TrackedRouteContext.Provider value={true}>
      {children}
    </TrackedRouteContext.Provider>
  );
};

export default useAnalytics;
export { TrackedRoute, usePageName, useTrackApplicationStart };
